import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import NavbarStaff from "../components/NavbarStaff";
import NavbarAdmin from "../components/NavbarAdmin";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import { formatBeds } from "../utils/beds";

const API = process.env.REACT_APP_API_URL || "";

const STATUS_CONFIG = {
  Pending:       { color: "#92400e", bg: "#fef3c7", dot: "#f59e0b" },
  Approved:      { color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  Confirmed:     { color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  CheckedIn:     { color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  CheckedOut:    { color: "#374151", bg: "#f3f4f6", dot: "#9ca3af" },
  PendingCancel: { color: "#6b21a8", bg: "#f3e8ff", dot: "#a855f7" },
  Cancelled:     { color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
};

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString("th-TH", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

export default function BookingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking,      setBooking]      = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [showCancel,   setShowCancel]   = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling,   setCancelling]   = useState(false);
  const [cancelError,  setCancelError]  = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMsg,     setActionMsg]     = useState("");

  const role    = localStorage.getItem("role");
  const isStaff = role === "staff" || role === "admin";
  const isAdmin = role === "admin";
  const { lang } = useLanguage();

  const handleCheckIn = async () => {
    try {
      setActionLoading(true); setActionMsg("");
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/checkin/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ paymentReceived: 0 }),
      });
      if (!res.ok) throw new Error("เช็คอินไม่สำเร็จ");
      setBooking(prev => ({ ...prev, status: "CheckedIn" }));
      setActionMsg("✅ เช็คอินสำเร็จ");
    } catch (err) {
      setActionMsg(`❌ ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckOut = () => navigate(`/admin/checkout/${id}`);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("token");
        if (!token) { setError("กรุณาเข้าสู่ระบบก่อน"); setLoading(false); return; }

        const res = await fetch(`${API}/api/bookings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error("ไม่พบข้อมูลการจอง");

        const data = await res.json();
        setBooking(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
  }, [id]);

  const NavBar = role === "admin" ? NavbarAdmin : role === "staff" ? NavbarStaff : Navbar;

  if (loading) return (
    <div style={styles.page}>
      <NavBar />
      <div style={styles.center}><p style={{ color: "#6b7280" }}>กำลังโหลด...</p></div>
    </div>
  );

  if (error) return (
    <div style={styles.page}>
      <NavBar />
      <div style={styles.center}><p style={{ color: "#ef4444" }}>⚠️ {error}</p></div>
    </div>
  );

  if (!booking) return null;

  const roomType  = booking.roomType;
  const roomUnit  = booking.roomUnit;
  const cfg       = STATUS_CONFIG[booking.status] || STATUS_CONFIG.Pending;
  const imageUrl  = roomType?.image ? `${API}/uploads/${roomType.image}` : null;
  const bedText   = formatBeds(lang, roomType?.beds);
  const nights    = booking.checkIn && booking.checkOut
    ? Math.round((new Date(booking.checkOut) - new Date(booking.checkIn)) / 86400000) : 0;
  const canCancel = booking.status === "Pending" || booking.status === "Confirmed";
  const isPendingCancel = booking.status === "PendingCancel";

  /* ยอดชำระจริง — สถานะ Approved บอกแค่ว่าห้องถูกจองไว้ ไม่ได้แปลว่าจ่ายเงินแล้ว */
  const totalDue  = Number(booking.totalAmount) || 0;
  const paidSoFar = Number(booking.paidAmount)  || 0;
  const remaining = Math.max(0, totalDue - paidSoFar);

  /* ข้อมูลผู้เข้าพักที่บันทึกตอนเช็คอิน — แสดงเฉพาะ staff/admin เมื่อมีข้อมูลแล้ว */
  const gi = booking.guestInfo || {};
  const guestRows = [
    [t(lang, "ciFieldName"),        gi.fullName],
    [t(lang, "ciIdCard"),           gi.idCard],
    [t(lang, "ciFieldAddress"),     gi.address],
    [t(lang, "ciFieldPhone"),       gi.phone],
    [t(lang, "ciFieldGender"),      gi.gender],
    [t(lang, "ciFieldBirthDate"),   gi.birthDate ? fmtDate(gi.birthDate) : ""],
    [t(lang, "ciFieldNationality"), gi.nationality],
    [t(lang, "ciFieldCountry"),     gi.country],
    [t(lang, "ciFieldExtra"),       gi.extra],
  ].filter(([, v]) => v && String(v).trim());
  const hasGuestInfo = isStaff && guestRows.length > 0;

  const handleCancel = async () => {
    if (!cancelReason.trim()) { setCancelError("กรุณากรอกเหตุผลการยกเลิก"); return; }
    try {
      setCancelling(true);
      setCancelError("");
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/bookings/${id}/cancel`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reason: cancelReason }),
      });
      if (!res.ok) throw new Error("ยกเลิกการจองไม่สำเร็จ");
      // เปลี่ยนเป็น PendingCancel รอแอดมินตรวจสอบ
      setBooking(prev => ({ ...prev, status: "PendingCancel" }));
      setShowCancel(false);
      setCancelReason("");
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        .bd-back:hover {
          border-color: #9ca3af;
          color: #111827;
        }
        @media (max-width: 640px) {
          .bd-container { flex-direction: column !important; }
          .bd-left  { width: 100% !important; border-right: none !important; border-bottom: 1px solid #f3f4f6; }
          .bd-left .bd-imgwrap { height: 200px !important; }
          .bd-right { padding: 20px 16px !important; }
        }
      `}</style>
      <NavBar />

      <main style={styles.main}>
        {/* Back button */}
        <button className="bd-back" style={styles.backBtn} onClick={() => navigate(-1)}>
          {t(lang,"back")}
        </button>

        <div className="bd-container" style={styles.container}>
          {/* ─── Left: Room image ─── */}
          <div className="bd-left" style={styles.leftCol}>
            <div className="bd-imgwrap" style={styles.imgWrap}>
              {imageUrl
                ? <img src={imageUrl} alt="ห้องพัก" style={styles.roomImg} />
                : <div style={styles.imgPlaceholder}>🛏️</div>
              }
            </div>

            {/* ข้อมูลที่กรอกตอนเช็คอิน */}
            {hasGuestInfo && (
              <div style={styles.guestCard}>
                <p style={styles.guestCardTitle}>👤 {t(lang, "guestInfoTitle")}</p>
                {guestRows.map(([label, value]) => (
                  <div style={styles.guestRow} key={label}>
                    <span style={styles.guestLabel}>{label}</span>
                    <span style={styles.guestValue}>{value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ─── Right: Info panel ─── */}
          <div className="bd-right" style={styles.rightCol}>
            {/* Header */}
            <div style={styles.infoHeader}>
              <h1 style={styles.roomTitle}>
                {t(lang, "roomNoLabel")} {roomUnit ? roomUnit.roomNumber : "-"}
                {roomType ? ` (${roomType.name})` : ""}
              </h1>
            </div>

            <div style={styles.divider} />

            {/* Highlights */}
            <div style={styles.highlightsGrid}>
              <HighlightItem icon="🍽️" text={t(lang,"breakfastIncl")} />
              <HighlightItem icon="👥" text={t(lang, "capacity", roomType?.adults ?? 1, roomType?.children ?? 0)} />
              {bedText && <HighlightItem icon="🛏️" text={bedText} />}
              <HighlightItem icon="🐾" text={t(lang,"noPets")} />
            </div>

            {/* Amenities */}
            {roomType?.amenities?.length > 0 && (
              <div style={styles.amenitiesRow}>
                {roomType.amenities.map((a, i) => (
                  <span key={i} style={styles.amenityTag}>{a}</span>
                ))}
              </div>
            )}

            <div style={styles.divider} />

            {/* Booking info */}
            <div style={styles.infoSection}>
              <p style={styles.sectionTitle}>{t(lang,"bookingSection")}</p>
              <div style={styles.infoGrid}>
                <InfoRow icon="📅" label={t(lang,"bookedAt")}      value={fmtDateTime(booking.createdAt)} />
                <InfoRow icon="🏨" label={t(lang,"checkInDate2")}  value={fmtDate(booking.checkIn)} />
                <InfoRow icon="🚪" label={t(lang,"checkOutDate2")} value={fmtDate(booking.checkOut)} />
                {nights > 0 && (
                  <InfoRow icon="🌙" label={t(lang,"nightsCount2")} value={`${nights} ${t(lang,"nightsUnit")}`} />
                )}
                {booking.totalAmount != null && (
                  <InfoRow icon="💰" label={t(lang,"totalAmount")} value={`฿${booking.totalAmount.toLocaleString()}`} highlight />
                )}
                {booking.totalAmount != null && (
                  <InfoRow icon="💳" label={t(lang,"paidAmountLbl")} value={`฿${paidSoFar.toLocaleString()}`} />
                )}
                {remaining > 0 && (
                  <InfoRow icon="⏳" label={t(lang,"remainingLbl")} value={`฿${remaining.toLocaleString()}`} statusColor="#b45309" />
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0" }}>
                  <span style={{ fontSize: 18, width: 22, textAlign: "center" }}>🔖</span>
                  <span style={{ fontSize: 15, color: "#6b7280", minWidth: 110 }}>{t(lang,"statusColon")} :</span>
                  <span style={{ fontSize: 17, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "4px 14px", borderRadius: 999 }}>{t(lang, booking.status)}</span>
                </div>
              </div>
            </div>

            {/* ── Next-step banner (staff/admin only) ── */}
            {isStaff && (() => {
              const s = booking.status;
              if (s === "Pending") return (
                <div style={styles.nextStepBanner("#fef9c3", "#713f12", "#fde68a")}>
                  <span style={{ fontSize: 20 }}>⏳</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{t(lang,"pendingPayment")}</div>
                    <div style={{ fontSize: 12, marginTop: 2, opacity: .8 }}>{t(lang,"pendingPaySub")}</div>
                  </div>
                  <button style={styles.nextBtn("#111827", "#fff")} onClick={() => navigate(isAdmin ? "/admin/payments" : "/slips")}>{t(lang,"checkSlip")}</button>
                </div>
              );
              if (s === "Approved") {
                /* ห้องถูกจองไว้แล้วก็จริง แต่ยอดเงินอาจยังไม่ครบ — อย่าบอกว่า "ชำระเงินสำเร็จ" ถ้ายังค้างอยู่ */
                const unpaid  = paidSoFar <= 0;
                const partial = !unpaid && remaining > 0;
                const title   = unpaid  ? t(lang,"unpaidReadyIn")
                              : partial ? t(lang,"partialReadyIn")
                              :           t(lang,"paidReadyIn");
                const sub = remaining > 0
                  ? `${t(lang,"remainingLbl")} ฿${remaining.toLocaleString()} · ${t(lang,"collectAtDesk")}`
                  : `${t(lang,"checkInDate2")} ${new Date(booking.checkIn).toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" })}`;
                const tone = remaining > 0
                  ? ["#fef9c3", "#713f12", "#fde68a"]
                  : ["#dcfce7", "#14532d", "#86efac"];
                return (
                  <div style={styles.nextStepBanner(...tone)}>
                    <span style={{ fontSize: 20 }}>{remaining > 0 ? "💰" : "✅"}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
                      <div style={{ fontSize: 12, marginTop: 2, opacity: .8 }}>{sub}</div>
                    </div>
                    <button style={styles.nextBtn("#16a34a", "#fff")} onClick={() => navigate(isAdmin ? "/admin/checkin" : "/checkin", { state: { bookingId: booking._id } })}>
                      {t(lang,"checkInBtn")}
                    </button>
                  </div>
                );
              }
              if (s === "Confirmed") return (
                <div style={styles.nextStepBanner("#dcfce7", "#14532d", "#86efac")}>
                  <span style={{ fontSize: 20 }}>✅</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{t(lang,"confirmedReadyIn")}</div>
                    <div style={{ fontSize: 12, marginTop: 2, opacity: .8 }}>{t(lang,"checkInDate2")} {new Date(booking.checkIn).toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" })}</div>
                  </div>
                  <button style={styles.nextBtn("#16a34a", "#fff")} onClick={() => navigate(isAdmin ? "/admin/checkin" : "/checkin", { state: { bookingId: booking._id } })}>
                    {t(lang,"checkInBtn")}
                  </button>
                </div>
              );
              if (s === "CheckedIn") return (
                <div style={styles.nextStepBanner("#dbeafe", "#1e3a8a", "#93c5fd")}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{t(lang,"stayingReadyOut")}</div>
                    <div style={{ fontSize: 12, marginTop: 2, opacity: .8 }}>{t(lang,"checkOutDate2")} {new Date(booking.checkOut).toLocaleDateString("th-TH", { day: "2-digit", month: "short", year: "numeric" })}</div>
                  </div>
                  <button style={styles.nextBtn("#1d4ed8", "#fff")} onClick={() => navigate(isAdmin ? "/admin/checkout" : "/checkout", { state: { bookingId: booking._id } })}>
                    {t(lang,"checkOutBtn")}
                  </button>
                </div>
              );
              if (s === "CheckedOut") return (
                <div style={styles.nextStepBanner("#f0fdf4", "#14532d", "#86efac")}>
                  <span style={{ fontSize: 20 }}>🎉</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{t(lang,"checkoutDone")}</div>
                    <div style={{ fontSize: 12, marginTop: 2, opacity: .8 }}>{t(lang,"stayComplete")}</div>
                  </div>
                </div>
              );
              if (s === "PendingCancel") return (
                <div style={styles.nextStepBanner("#f3e8ff", "#581c87", "#d8b4fe")}>
                  <span style={{ fontSize: 20 }}>🕐</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{t(lang,"pendingCancelTitle")}</div>
                    <div style={{ fontSize: 12, marginTop: 2, opacity: .8 }}>{t(lang,"pendingCancelSub")}</div>
                  </div>
                  <button style={styles.nextBtn("#7c3aed", "#fff")} onClick={() => navigate(isAdmin ? "/admin/cancellations" : "/cancellations")}>{t(lang,"reviewCancel")}</button>
                </div>
              );
              return null;
            })()}

            {actionMsg && (
              <div style={{ margin: "8px 0", padding: "10px 14px", borderRadius: 10,
                background: actionMsg.startsWith("✅") ? "#f0fdf4" : "#fef2f2",
                color: actionMsg.startsWith("✅") ? "#14532d" : "#991b1b",
                fontSize: 13, fontWeight: 600 }}>
                {actionMsg}
              </div>
            )}

            {/* Cancel button */}
            {canCancel && (
              <>
                <div style={styles.divider} />
                <div style={styles.actionRow}>
                  <button style={styles.cancelBtn} onClick={() => { setShowCancel(true); setCancelError(""); }}>
                    {t(lang,"cancelBtn")}
                  </button>
                </div>
              </>
            )}

            {isPendingCancel && (
              <div style={styles.pendingCancelBanner}>
                <span style={{ fontSize: 16 }}>🕐</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#6b21a8" }}>ส่งคำขอยกเลิกแล้ว</div>
                  <div style={{ fontSize: 13, color: "#7e22ce", marginTop: 2 }}>กำลังรอแอดมินตรวจสอบ อาจใช้เวลา 1-3 วันทำการ</div>
                </div>
              </div>
            )}

            {/* Cancel reason modal */}
            {showCancel && (
              <div style={modal.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) e.currentTarget._ok = true; }} onMouseUp={(e) => { if (e.currentTarget._ok && e.target === e.currentTarget) setShowCancel(false); e.currentTarget._ok = false; }}>
                <div style={modal.box} onClick={e => e.stopPropagation()}>
                  <h3 style={modal.title}>ยกเลิกการจอง</h3>
                  <p style={modal.sub}>กรุณาระบุเหตุผลในการยกเลิก</p>
                  <textarea
                    style={modal.textarea}
                    placeholder="เช่น ติดธุระฉุกเฉิน, เปลี่ยนแปลงแผนการเดินทาง..."
                    rows={4}
                    value={cancelReason}
                    onChange={e => { setCancelReason(e.target.value); setCancelError(""); }}
                  />
                  {cancelError && <p style={modal.errText}>{cancelError}</p>}
                  <div style={modal.btnRow}>
                    <button style={modal.btnGhost} onClick={() => setShowCancel(false)}>ยกเลิก</button>
                    <button style={modal.btnRed} onClick={handleCancel} disabled={cancelling}>
                      {cancelling ? "กำลังดำเนินการ..." : "ยืนยันการยกเลิก"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function HighlightItem({ icon, text }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151" }}>
      <span style={{ fontSize: 15 }}>{icon}</span>
      <span>{text}</span>
    </div>
  );
}

function InfoRow({ icon, label, value, highlight, statusColor }) {
  return (
    <div style={styles.infoRow}>
      <span style={{ fontSize: 15, width: 22, textAlign: "center" }}>{icon}</span>
      <span style={{ fontSize: 14, color: "#6b7280", minWidth: 110 }}>{label} :</span>
      <span style={{
        fontSize: 14,
        color: statusColor || "#111827",
        fontWeight: highlight || statusColor ? 700 : 600,
      }}>
        {value}
      </span>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f0f0f0",
    fontFamily: "'Noto Sans Thai', sans-serif",
    display: "flex",
    flexDirection: "column",
  },
  main: {
    flex: 1,
    padding: "20px 32px 40px",
    maxWidth: 1000,
    margin: "0 auto",
    width: "100%",
    boxSizing: "border-box",
  },
  center: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "80px 0",
  },
  backBtn: {
    background: "transparent",
    border: "1px solid #d1d5db",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 14,
    color: "#374151",
    padding: "7px 14px",
    marginBottom: 16,
    fontWeight: 600,
    fontFamily: "inherit",
    transition: "border-color .15s, color .15s",
  },
  container: {
    background: "rgba(255,255,255,0.55)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: "1px solid rgba(255,255,255,0.75)",
    borderRadius: 20,
    boxShadow: "0 8px 32px rgba(0,0,0,0.10), 0 1px 0 rgba(255,255,255,0.8) inset",
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    minHeight: 480,
  },

  /* Left col */
  leftCol: {
    width: 280,
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    borderRight: "1px solid #f3f4f6",
  },
  imgWrap: {
    width: "100%",
    height: 220,
    overflow: "hidden",
    background: "#f3f4f6",
  },
  roomImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  imgPlaceholder: {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 52,
    color: "#9ca3af",
  },
  guestCard: {
    padding: "16px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  guestCardTitle: {
    margin: "0 0 4px",
    fontSize: 13,
    fontWeight: 700,
    color: "#374151",
  },
  guestRow: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  guestLabel: {
    fontSize: 11,
    color: "#9ca3af",
    fontWeight: 600,
  },
  guestValue: {
    fontSize: 13,
    color: "#111827",
    fontWeight: 500,
    wordBreak: "break-word",
  },
  statusCard: {
    padding: "20px 20px",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  statusLabel: {
    margin: 0,
    fontSize: 12,
    color: "#9ca3af",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  statusBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    fontSize: 13,
    fontWeight: 700,
    padding: "6px 14px",
    borderRadius: 999,
    alignSelf: "flex-start",
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },

  /* Right col */
  rightCol: {
    flex: 1,
    padding: "28px 32px 28px",
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  infoHeader: {
    marginBottom: 4,
  },
  roomTitle: {
    margin: 0,
    fontSize: 24,
    fontWeight: 800,
    color: "#111",
  },
  divider: {
    height: 1,
    background: "#f3f4f6",
    margin: "16px 0",
  },
  highlightsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px 16px",
    marginBottom: 12,
  },
  amenitiesRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 4,
  },
  amenityTag: {
    fontSize: 12,
    background: "#f3f4f6",
    color: "#374151",
    padding: "3px 10px",
    borderRadius: 999,
  },
  infoSection: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  sectionTitle: {
    margin: "0 0 10px",
    fontSize: 13,
    fontWeight: 700,
    color: "#9ca3af",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  infoGrid: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  infoRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "7px 0",
    borderBottom: "1px solid #f9fafb",
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    paddingTop: 4,
  },
  cancelBtn: {
    background: "#ef4444",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    padding: "12px 32px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
  },
  pendingCancelBanner: {
    display: "flex", alignItems: "flex-start", gap: 10,
    background: "#f3e8ff", border: "1px solid #d8b4fe",
    borderRadius: 12, padding: "14px 16px", marginTop: 16,
  },
  nextStepBanner: (bg, color, border) => ({
    display: "flex", alignItems: "center", justifyContent: "space-between",
    flexWrap: "wrap", gap: 10,
    background: bg, border: `1px solid ${border}`, color,
    borderRadius: 12, padding: "14px 18px", marginTop: 16,
  }),
  nextBtn: (bg, color) => ({
    background: bg, color,
    border: "none", borderRadius: 10,
    padding: "10px 22px", fontSize: 14, fontWeight: 700,
    cursor: "pointer", whiteSpace: "nowrap",
  }),
};

const modal = {
  overlay: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 1000,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  box: {
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(20px) saturate(180%)",
    WebkitBackdropFilter: "blur(20px) saturate(180%)",
    border: "1px solid rgba(255,255,255,0.75)",
    borderRadius: 16, padding: "28px 28px 24px",
    width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,.2)",
  },
  title: {
    margin: "0 0 6px", fontSize: 18, fontWeight: 700, color: "#111",
  },
  sub: {
    margin: "0 0 16px", fontSize: 14, color: "#6b7280",
  },
  textarea: {
    width: "100%", borderRadius: 10, border: "1px solid #e5e7eb",
    padding: "10px 12px", fontSize: 14, fontFamily: "inherit",
    resize: "vertical", outline: "none", boxSizing: "border-box",
    color: "#111", lineHeight: 1.6,
  },
  errText: {
    margin: "8px 0 0", fontSize: 13, color: "#ef4444",
  },
  btnRow: {
    display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20,
  },
  btnGhost: {
    background: "none", border: "1px solid #e5e7eb", borderRadius: 10,
    padding: "10px 22px", fontSize: 14, fontWeight: 600, cursor: "pointer", color: "#374151",
  },
  btnRed: {
    background: "#ef4444", color: "#fff", border: "none", borderRadius: 10,
    padding: "10px 22px", fontSize: 14, fontWeight: 700, cursor: "pointer",
  },
};